<template>
  <div class="ib-tab" data-component-id="insights--panel--browser">

    <!-- Filter bar -->
    <div class="ib-filters">
      <div class="ib-status-pills">
        <button
          v-for="opt in STATUS_OPTIONS"
          :key="opt.code"
          class="ib-pill"
          :class="['pill-' + opt.code, { active: filters.status === opt.code }]"
          @click="setStatus(opt.code)"
        >
          {{ opt.label }}
          <span v-if="statusCounts[opt.code] != null" class="ib-pill-count">{{ statusCounts[opt.code] }}</span>
        </button>
      </div>

      <div class="ib-row">
        <select v-model="filters.category" @change="reload" class="ib-input">
          <option value="">Semua kategori</option>
          <option v-for="c in categories" :key="c.code" :value="c.code">
            {{ c.code }} ({{ c.name }})
          </option>
        </select>
        <input
          v-model="filters.query"
          @keyup.enter="reload"
          class="ib-input ib-input-search"
          placeholder="Cari subject / statement…"
        />
        <button class="ib-mini" @click="reload">↻ Cari</button>
      </div>
    </div>

    <div v-if="loadError" class="ib-error">{{ loadError }}</div>

    <!-- Two-pane layout -->
    <div class="ib-pane">
      <!-- LEFT: list -->
      <div class="ib-list-pane">
        <div class="ib-list-header">
          <span>{{ insights.length }} insight</span>
          <span v-if="loading" class="ib-loading">memuat…</span>
        </div>
        <div v-if="!insights.length && !loading" class="ib-empty">
          Tidak ada insight pada filter ini.
        </div>
        <ul class="ib-list">
          <li
            v-for="ins in insights"
            :key="ins.insightId"
            class="ib-item"
            :class="{ selected: selected?.insightId === ins.insightId, ['status-'+ins.status]: true }"
            @click="select(ins.insightId)"
          >
            <div class="ib-item-head">
              <span class="ib-conf" :class="'conf-'+ins.confidence">{{ confLetter(ins.confidence) }}</span>
              <span class="ib-cat">{{ ins.categoryCode }}</span>
              <span class="ib-status-mini" :class="'sm-'+ins.status">{{ ins.status }}</span>
              <span class="ib-id">#{{ ins.insightId }}</span>
            </div>
            <div class="ib-subject">{{ ins.subject }}</div>
            <div class="ib-statement">{{ truncate(ins.statement, 130) }}</div>
            <div class="ib-meta">
              <span v-if="ins.tags" class="ib-tags">🏷 {{ ins.tags }}</span>
              <span class="ib-date">{{ formatDate(ins.createdAt) }}</span>
            </div>
          </li>
        </ul>
      </div>

      <!-- RIGHT: detail -->
      <div class="ib-detail-pane">
        <div v-if="!selected" class="ib-detail-empty">
          Pilih insight di kiri untuk lihat statement lengkap & evidence.
        </div>
        <div v-else class="ib-detail">
          <div class="ib-detail-head">
            <div>
              <span class="ib-conf" :class="'conf-'+selected.confidence">{{ confLetter(selected.confidence) }}</span>
              <strong>#{{ selected.insightId }} · {{ selected.subject }}</strong>
            </div>
            <span class="ib-status-pill" :class="'sm-'+selected.status">{{ selected.status }}</span>
          </div>

          <div class="ib-detail-grid">
            <div><span>Kategori</span><strong>{{ selected.categoryCode }}</strong></div>
            <div><span>Confidence</span><strong>{{ selected.confidence }}</strong></div>
            <div><span>Version</span><strong>v{{ selected.version }}</strong></div>
            <div><span>Sample</span><strong>{{ selected.sampleCount ?? '-' }}</strong></div>
            <div><span>First seen</span><strong>{{ selected.firstSeen?.slice(0,10) || '-' }}</strong></div>
            <div><span>Last seen</span><strong>{{ selected.lastSeen?.slice(0,10) || '-' }}</strong></div>
            <div><span>Created by</span><strong>{{ truncate(selected.createdBy, 24) }}</strong></div>
            <div v-if="selected.approvedBy"><span>Approved by</span><strong>{{ selected.approvedBy }}</strong></div>
          </div>

          <div class="ib-section">
            <div class="ib-section-title">Statement</div>
            <p class="ib-statement-full">{{ selected.statement }}</p>
          </div>

          <div v-if="selected.tags" class="ib-section">
            <div class="ib-section-title">Tags</div>
            <div class="ib-chip-row">
              <span v-for="t in tagList(selected.tags)" :key="t" class="ib-chip">{{ t }}</span>
            </div>
          </div>

          <div class="ib-section">
            <div class="ib-section-title">Evidence ({{ evidence.length }} kutipan chat — sudah di-redact PII)</div>
            <ul v-if="evidence.length" class="ib-evidence">
              <li v-for="e in evidence" :key="e.evidenceId">
                <div class="ib-ev-meta">
                  <span class="ib-ev-type">{{ e.sampleType }}</span>
                  <span v-if="e.occurredAt" class="ib-ev-date">{{ formatDate(e.occurredAt) }}</span>
                </div>
                <div class="ib-ev-text">{{ e.sampleText }}</div>
              </li>
            </ul>
            <div v-else class="ib-empty">Tidak ada evidence tersimpan.</div>
          </div>

          <!-- Action buttons -->
          <div class="ib-actions">
            <button
              v-if="selected.status !== 'approved'"
              class="ib-btn ib-btn-approve"
              :disabled="acting"
              @click="approve"
            >{{ acting === 'approve' ? '⏳' : '✓' }} Approve</button>
            <button
              v-if="selected.status !== 'rejected'"
              class="ib-btn ib-btn-reject"
              :disabled="acting"
              @click="reject"
            >{{ acting === 'reject' ? '⏳' : '✗' }} Reject</button>
            <button class="ib-mini" @click="reloadDetail">↻ Refresh</button>
          </div>
          <div v-if="actionError" class="ib-error">{{ actionError }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'

const STORAGE_KEY = 'uprint_auth_token'

const STATUS_OPTIONS = [
  { code: 'all',      label: 'Semua' },
  { code: 'draft',    label: 'Draft' },
  { code: 'approved', label: 'Approved' },
  { code: 'rejected', label: 'Rejected' },
  { code: 'stale',    label: 'Stale' },
]

const filters = reactive({ status: 'draft', category: '', query: '' })
const insights      = ref([])
const categories    = ref([])
const statusCounts  = ref({})
const loading       = ref(false)
const loadError     = ref(null)

const selected      = ref(null)
const evidence      = ref([])
const acting        = ref(null)
const actionError   = ref(null)

function token() { return localStorage.getItem(STORAGE_KEY) || '' }

function truncate(s, n) {
  if (!s) return ''
  return s.length > n ? s.slice(0, n) + '…' : s
}

function confLetter(c) {
  return ({ low: 'L', medium: 'M', high: 'H' })[c] || '?'
}

function tagList(tags) {
  return String(tags || '').split(',').map(t => t.trim()).filter(Boolean)
}

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const now = Date.now()
  const diff = now - d.getTime()
  if (diff < 60_000)        return Math.floor(diff/1000) + 's lalu'
  if (diff < 3600_000)      return Math.floor(diff/60_000) + 'm lalu'
  if (diff < 86400_000)     return Math.floor(diff/3600_000) + 'j lalu'
  if (diff < 86400_000 * 7) return Math.floor(diff/86400_000) + 'h lalu'
  return d.toISOString().slice(0, 10)
}

function setStatus(code) {
  if (filters.status === code) return
  filters.status = code
  reload()
}

async function reload() {
  const t = token()
  if (!t) {
    loadError.value = 'Token admin tidak tersedia.'
    return
  }
  loading.value = true
  loadError.value = null
  try {
    const params = new URLSearchParams({
      token: t,
      status: filters.status,
      limit: '50',
    })
    if (filters.category) params.set('category', filters.category)
    if (filters.query)    params.set('query', filters.query.trim())
    const resp = await fetch(`/api/insights?${params.toString()}`)
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || `HTTP ${resp.status}`)
    insights.value = data.insights || []
  } catch (err) {
    loadError.value = err.message
  } finally {
    loading.value = false
  }
}

async function loadCategoriesAndStats() {
  const t = token()
  if (!t) return
  try {
    const resp = await fetch(`/api/insights/categories?token=${encodeURIComponent(t)}`)
    const data = await resp.json()
    if (!data.success) return
    categories.value = data.categories || []
    const bs = data.stats?.byStatus || {}
    statusCounts.value = {
      all:      data.stats?.totalInsights || 0,
      draft:    bs.draft    || 0,
      approved: bs.approved || 0,
      rejected: bs.rejected || 0,
      stale:    bs.stale    || 0,
    }
  } catch (err) {
    /* tolerable */
  }
}

async function select(id) {
  const t = token()
  selected.value = null
  evidence.value = []
  actionError.value = null
  try {
    const resp = await fetch(`/api/insights/${id}?token=${encodeURIComponent(t)}`)
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || `HTTP ${resp.status}`)
    selected.value = data.insight
    evidence.value = data.evidence || []
  } catch (err) {
    loadError.value = err.message
  }
}

async function reloadDetail() {
  if (selected.value?.insightId) await select(selected.value.insightId)
}

async function approve() {
  if (!selected.value || acting.value) return
  acting.value = 'approve'
  actionError.value = null
  try {
    const t = token()
    const resp = await fetch(`/api/insights/${selected.value.insightId}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t }),
    })
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || `HTTP ${resp.status}`)
    await Promise.all([reloadDetail(), reload(), loadCategoriesAndStats()])
  } catch (err) {
    actionError.value = err.message
  } finally {
    acting.value = null
  }
}

async function reject() {
  if (!selected.value || acting.value) return
  const note = window.prompt('Catatan reject (opsional):', '')
  if (note === null) return
  acting.value = 'reject'
  actionError.value = null
  try {
    const t = token()
    const resp = await fetch(`/api/insights/${selected.value.insightId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t, note }),
    })
    const data = await resp.json()
    if (!data.success) throw new Error(data.message || `HTTP ${resp.status}`)
    await Promise.all([reloadDetail(), reload(), loadCategoriesAndStats()])
  } catch (err) {
    actionError.value = err.message
  } finally {
    acting.value = null
  }
}

onMounted(async () => {
  await loadCategoriesAndStats()
  await reload()
})
</script>

<style scoped>
.ib-tab {
  font-size: 12px;
  color: #d4d4d4;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;
}

/* Filters */
.ib-filters { display: flex; flex-direction: column; gap: 8px; }
.ib-status-pills { display: flex; gap: 4px; flex-wrap: wrap; }
.ib-pill {
  padding: 4px 10px;
  background: #161616; border: 1px solid #2a2a2a; color: #aaa;
  border-radius: 999px; cursor: pointer; font-size: 11px;
  display: inline-flex; align-items: center; gap: 5px;
  transition: background .15s;
}
.ib-pill:hover { background: #1c1c1c; color: #e5e5e5; }
.ib-pill.active { background: #1d3a23; border-color: #22c55e; color: #86efac; }
.ib-pill-count {
  background: #0a0a0a; padding: 1px 6px; border-radius: 999px;
  font-size: 9px; color: #888;
}
.ib-pill.active .ib-pill-count { background: #052e0d; color: #4ade80; }

.ib-row { display: flex; gap: 6px; align-items: center; }
.ib-input {
  background: #0d0d0d; border: 1px solid #2a2a2a; color: #e5e5e5;
  border-radius: 4px; padding: 5px 7px; font-size: 11px;
}
.ib-input-search { flex: 1; }
.ib-mini {
  background: #1a1a1a; border: 1px solid #2a2a2a; color: #aaa;
  font-size: 10px; padding: 3px 9px; border-radius: 4px; cursor: pointer;
}
.ib-mini:hover { background: #222; color: #e5e5e5; }

/* Two-pane */
.ib-pane {
  display: grid; grid-template-columns: minmax(240px, 38%) 1fr;
  gap: 8px; min-height: 360px;
}
@media (max-width: 760px) { .ib-pane { grid-template-columns: 1fr; } }

.ib-list-pane {
  background: #0e0e0e; border: 1px solid #1f1f1f; border-radius: 6px;
  display: flex; flex-direction: column; max-height: 540px;
}
.ib-list-header {
  display: flex; justify-content: space-between; padding: 6px 10px;
  border-bottom: 1px solid #1f1f1f; font-size: 10px; color: #888;
}
.ib-loading { color: #fbbf24; }
.ib-empty { color: #666; padding: 20px; text-align: center; font-style: italic; font-size: 11px; }
.ib-list { list-style: none; margin: 0; padding: 0; overflow: auto; flex: 1; }

.ib-item {
  padding: 8px 10px;
  border-bottom: 1px solid #161616;
  cursor: pointer;
  transition: background .15s;
}
.ib-item:hover { background: #131313; }
.ib-item.selected { background: #122418; border-left: 3px solid #22c55e; padding-left: 7px; }

.ib-item-head { display: flex; align-items: center; gap: 6px; font-size: 10px; }
.ib-cat { color: #86efac; font-weight: 600; font-family: ui-monospace, monospace; }
.ib-id { margin-left: auto; color: #666; font-family: ui-monospace, monospace; font-size: 9px; }
.ib-subject { font-size: 11px; font-weight: 600; color: #e5e5e5; margin: 3px 0 2px; }
.ib-statement { color: #aaa; font-size: 11px; line-height: 1.4; }
.ib-meta { display: flex; gap: 8px; margin-top: 4px; font-size: 9px; color: #666; flex-wrap: wrap; }
.ib-tags { color: #a78bfa; }

.ib-conf {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%; font-size: 9px; font-weight: 700;
}
.conf-low    { background: #3a2d1e; color: #fbbf24; }
.conf-medium { background: #1e3a8a; color: #93c5fd; }
.conf-high   { background: #1d3a23; color: #86efac; }

.ib-status-mini {
  font-size: 9px; padding: 1px 6px; border-radius: 3px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
}
.sm-draft     { background: #3a2d1e; color: #fbbf24; }
.sm-approved  { background: #1d3a23; color: #86efac; }
.sm-rejected  { background: #3b1212; color: #fca5a5; }
.sm-stale     { background: #2a2a2a; color: #a3a3a3; }

/* Detail pane */
.ib-detail-pane {
  background: #0e0e0e; border: 1px solid #1f1f1f; border-radius: 6px;
  padding: 12px; max-height: 540px; overflow: auto;
}
.ib-detail-empty {
  color: #666; padding: 60px 20px; text-align: center; font-style: italic;
}
.ib-detail { display: flex; flex-direction: column; gap: 12px; }
.ib-detail-head {
  display: flex; justify-content: space-between; gap: 8px;
  align-items: center; flex-wrap: wrap;
}
.ib-detail-head strong { font-size: 12px; }
.ib-status-pill {
  font-size: 10px; padding: 3px 9px; border-radius: 999px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.4px;
}

.ib-detail-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 16px;
  font-size: 11px;
  background: #131313; padding: 8px 10px; border-radius: 4px;
}
.ib-detail-grid div { display: flex; justify-content: space-between; gap: 8px; }
.ib-detail-grid span { color: #888; }
.ib-detail-grid strong { color: #e5e5e5; font-weight: 500; }

.ib-section { display: flex; flex-direction: column; gap: 5px; }
.ib-section-title { font-size: 10px; color: #888; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
.ib-statement-full {
  margin: 0; line-height: 1.5; color: #d4d4d4; font-size: 12px;
  background: #131313; padding: 10px; border-radius: 4px; border-left: 2px solid #22c55e;
}

.ib-chip-row { display: flex; flex-wrap: wrap; gap: 4px; }
.ib-chip {
  background: #1a1a1a; border: 1px solid #2a2a2a; padding: 2px 8px;
  border-radius: 999px; font-size: 10px; color: #a78bfa;
}

.ib-evidence { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.ib-evidence li {
  background: #131313; border-radius: 4px; padding: 6px 10px;
  border-left: 2px solid #2a2a2a;
}
.ib-ev-meta { display: flex; gap: 8px; font-size: 9px; color: #666; margin-bottom: 3px; }
.ib-ev-type { color: #a78bfa; }
.ib-ev-text { font-size: 11px; line-height: 1.45; color: #ccc; white-space: pre-wrap; }

.ib-actions { display: flex; gap: 6px; align-items: center; padding-top: 4px; border-top: 1px solid #1f1f1f; padding-top: 10px; }
.ib-btn {
  padding: 6px 12px; border-radius: 4px; border: none; cursor: pointer;
  font-size: 11px; font-weight: 600;
}
.ib-btn:disabled { opacity: 0.5; cursor: wait; }
.ib-btn-approve { background: #1d3a23; color: #86efac; border: 1px solid #16a34a; }
.ib-btn-approve:hover:not(:disabled) { background: #16a34a; color: #052e0d; }
.ib-btn-reject  { background: #3b1212; color: #fca5a5; border: 1px solid #b91c1c; }
.ib-btn-reject:hover:not(:disabled)  { background: #b91c1c; color: #fee2e2; }

.ib-error {
  background: #3b1212; border: 1px solid #7a1f1f; color: #fca5a5;
  padding: 6px 10px; border-radius: 4px; font-size: 11px;
}
</style>
